/**
 * 导入模型 cache 的 publish-scope 契约。
 *
 * 背景：跨 Tab 加载装配体 / 插入 CAD 等事务性链路需要把"同 partName/version 但归属
 * 不同发布作用域"的 PartAsset / AssemblyModelAsset 写入同一个内存 cache 而不相互覆盖。
 *
 * 本模块只定义 cad-core 最小契约（**key 契约**）：
 *   - 默认 publish scope 常量（`'project'`）
 *   - scope key 类型（一律使用 string；具体构造规则由宿主侧 —— web-client 的
 *     `imported-model-load-scope.ts` —— 负责，cad-core 不反向依赖宿主上下文）
 *   - 共享 key helper，用于在 cache 内部拼出稳定、可读的复合 key
 *
 * 为什么放在 cad-core（key 契约）而不是 web-client（host scope 组合）：
 *   - `PartAssetCache` / `AssemblyModelCache` 自身在 cad-core，其 `set/get/peek/delete`
 *     签名必须把 scope 作为一等入参；若把 scope 常量/类型塞到 web-client，会出现
 *     "cad-core 的 cache 接口反向依赖 web-client"。
 *   - read-side consumer（`tab-type.ts`）也在 cad-core，且必须能在**不知道宿主上下文**
 *     的前提下回退到默认 project scope。
 *   - web-client 的 `imported-model-load-scope.ts` 只负责按宿主上下文派生具体字符串
 *     （例如 `'insert-tx-<uuid>'`），不拥有这个契约本身。
 *
 * 默认回退约定：
 *   - cache 的 `set/get/peek/delete` 在 scope 参数为 `undefined` 时，一律视作
 *     `DEFAULT_IMPORTED_MODEL_PUBLISH_SCOPE`（即 `'project'`）。
 *   - read-side consumer（`tab-type.ts` 的 `resolveImportedModelTabTypeFromCaches` /
 *     `applyImportedModelTabCacheState`）必须**显式**传入默认 project scope，不允许依赖
 *     "省略参数碰巧命中"的兜底行为——这是为了在 grep/阅读时能直观看到 "这里读的是
 *     project 域"，而不是偶然对齐。
 */

/**
 * publish scope 的运行时 key。宿主侧可按需要生成任意字符串（例如
 * `'project'`、`'insert-tx-<uuid>'`）。cad-core 不解析该字符串，只做等值比较。
 */
export type ImportedModelPublishScopeKey = string

/**
 * 默认 publish scope —— 对应"项目全局可见的已发布模型"，也是当前语义同步链路
 * （useEditorGlbLoader / useProjectLoader 等）的落点。
 */
export const DEFAULT_IMPORTED_MODEL_PUBLISH_SCOPE: ImportedModelPublishScopeKey = 'project'

/**
 * 规整 scope 入参。`undefined` 时落到默认 project scope。
 *
 * 模块私有 helper：仅供本文件的 `composeImportedModelCacheKey` 使用。外部调用方
 * 应直接传 scope 给 cache API 而不是自行规整。
 */
function normalizeImportedModelPublishScopeKey(
  scope: ImportedModelPublishScopeKey | undefined,
): ImportedModelPublishScopeKey {
  return scope ?? DEFAULT_IMPORTED_MODEL_PUBLISH_SCOPE
}

/**
 * 拼出 cache 内部复合 key。scope 位于最后一段，便于肉眼阅读。
 */
export function composeImportedModelCacheKey(
  fileUuid: string,
  version: string,
  scope: ImportedModelPublishScopeKey | undefined,
): `${string}:${string}:${string}` {
  return `${fileUuid}:${version}:${normalizeImportedModelPublishScopeKey(scope)}`
}
